import React from 'react';
import { getConfidenceClass } from '../utils/cardMetrics';

const getValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

const clampNumber = (value, fallback = 0) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(98, n));
};

const getEntityName = (entity, ...fallbacks) => {
  const direct = getValue(
    typeof entity === 'string' ? entity : null,
    entity?.name,
    entity?.teamName,
    entity?.fullName,
    entity?.shortName,
    ...fallbacks
  );

  const text = String(direct || '').trim();
  return text || 'Takim bilgisi bekleniyor';
};

const formatPercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 'Veri yok';

  const safe = Math.min(98, n);
  return `%${safe % 1 === 0 ? safe.toFixed(0) : safe.toFixed(1)}`;
};

const getRiskTone = (riskLevel) => {
  const text = String(riskLevel || '').toLocaleLowerCase('tr-TR');

  if (text.includes('yüksek') || text.includes('yuksek')) return 'high';
  if (text.includes('orta')) return 'mid';
  if (text.includes('düşük') || text.includes('dusuk')) return 'low';

  return 'mid';
};

const RiskyAnalysisCard = ({ analysis, matchTime }) => {
  if (!analysis) return null;

  const homeName = getEntityName(
    analysis.homeTeam,
    analysis.homeTeamName,
    analysis.match?.homeTeam?.name,
    analysis.match?.home_name,
    analysis.home_name,
    analysis.home
  );
  const awayName = getEntityName(
    analysis.awayTeam,
    analysis.awayTeamName,
    analysis.match?.awayTeam?.name,
    analysis.match?.away_name,
    analysis.away_name,
    analysis.away
  );
  const leagueName = getEntityName(
    analysis.league,
    analysis.leagueName,
    analysis.competition,
    analysis.tournament
  );

  const selectionLabel = String(
    getValue(analysis.selection, analysis.market?.label, analysis.market?.name, 'Tahmin bilgisi bekleniyor')
  ).trim();

  const confidenceValue = clampNumber(analysis.confidence);
  const modelValue = clampNumber(analysis.modelProbability);
  const riskLabel = analysis.riskLevel || 'Riskli';
  const riskTone = getRiskTone(riskLabel);

  const cleanedReasons = (Array.isArray(analysis.dataReasons) ? analysis.dataReasons : []).filter(
    (reason) => !/fallback/i.test(String(reason || ''))
  );
  const aiNote = String(getValue(analysis.aiInsight, analysis.reason, '') || '').trim();
  const visibleAiNote = aiNote && !/fallback/i.test(aiNote) ? aiNote : '';

  return (
    <article className={`analysis-card risky-card risky-premium-card risky-premium-card-v2 risk-tone-${riskTone}`}>
      <div className="risky-card-orbit" aria-hidden="true" />

      <div className="risky-topline">
        <span className="risky-league-pill">{leagueName}</span>
        <span className={`risky-risk-pill risk-${riskTone}`}>{riskLabel}</span>
      </div>

      <div className="risky-match-line risky-match-line-premium">
        <span className="risky-match-team">{homeName}</span>
        <span className="risky-match-separator">VS</span>
        <span className="risky-match-team">{awayName}</span>
      </div>

      <section className="risky-pick-panel">
        <div className="risky-pick-copy">
          <span className="risky-kicker">AI RISK EDGE</span>
          <strong>Riskli Denenebilir</strong>
          <small>{matchTime || 'Saat bilgisi bekleniyor'}</small>
        </div>

        <div className="risky-selection-stack">
          <span>Seçim</span>
          <b>{selectionLabel}</b>
        </div>
      </section>

      <div className="risky-confidence-panel">
        <div>
          <span>Güven Skoru</span>
          <strong className={getConfidenceClass(Math.round(confidenceValue))}>{formatPercent(confidenceValue)}</strong>
        </div>
        <div className="risky-progress-track" aria-hidden="true">
          <i style={{ width: `${confidenceValue}%` }} />
        </div>
      </div>

      <div className="risky-metric-grid risky-metric-grid-premium">
        <div className="risky-metric-box">
          <span>Model</span>
          <strong>{formatPercent(modelValue)}</strong>
          <div className="risky-mini-bar"><i style={{ width: `${modelValue}%` }} /></div>
        </div>

        <div className="risky-metric-box risky-metric-box-highlight">
          <span>Tahmin</span>
          <strong className="risky-prediction-led">{selectionLabel}</strong>
        </div>

        <div className="risky-metric-box">
          <span>Güven</span>
          <strong className={getConfidenceClass(Math.round(confidenceValue))}>{formatPercent(confidenceValue)}</strong>
          <div className="risky-mini-bar risky-mini-bar-green"><i style={{ width: `${confidenceValue}%` }} /></div>
        </div>

        <div className="risky-metric-box">
          <span>Risk</span>
          <strong className={`risky-risk-text risk-${riskTone}`}>{riskLabel}</strong>
        </div>
      </div>

      {cleanedReasons.length ? (
        <div className="risky-reasons risky-premium-reasons">
          <span>Veri Nedeni</span>
          <ul>
            {cleanedReasons.slice(0, 3).map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {visibleAiNote ? (
        <div className="risky-ai-note risky-premium-note">
          <span>Analiz Notu</span>
          <p>{visibleAiNote}</p>
        </div>
      ) : null}
    </article>
  );
};

export default RiskyAnalysisCard;
